import { useState, useRef, useCallback, useEffect } from 'react';
import type { TypingLesson, TypingDrill, TypingAttempt } from '../../lib/types';
import { saveTypingAttempt, saveProgress } from '../../lib/db';
import { askTutor } from '../../lib/ai';

interface TypingPracticeProps {
  lesson: TypingLesson | TypingDrill;
  onBack: () => void;
}

type Phase = 'ready' | 'typing' | 'done';

export default function TypingPractice({ lesson, onBack }: TypingPracticeProps) {
  const target = lesson.text;
  const [typed, setTyped] = useState('');
  const [phase, setPhase] = useState<Phase>('ready');
  const [startTime, setStartTime] = useState<number | null>(null);
  const [result, setResult] = useState<{ wpm: number; accuracy: number } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // AI tutor state
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState<string | null>(null);

  const isLesson = 'instruction' in lesson;

  // Focus input on mount and when resetting
  useEffect(() => {
    if (phase !== 'done') {
      inputRef.current?.focus();
    }
  }, [phase]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (phase === 'done') return;

      const value = e.target.value;

      // Start timer on first keypress
      if (phase === 'ready' && value.length > 0) {
        setPhase('typing');
        setStartTime(Date.now());
      }

      // Don't allow typing beyond target length
      if (value.length > target.length) return;

      setTyped(value);

      // Check completion
      if (value.length === target.length) {
        const endTime = Date.now();
        const elapsedMs = endTime - (startTime ?? endTime);
        const elapsedMin = Math.max(elapsedMs / 60000, 0.01); // avoid division by zero

        let correctChars = 0;
        for (let i = 0; i < target.length; i++) {
          if (value[i] === target[i]) correctChars++;
        }

        const wpm = Math.round((target.length / 5) / elapsedMin);
        const accuracy = Math.round((correctChars / target.length) * 100);

        setResult({ wpm, accuracy });
        setPhase('done');

        // Save to IndexedDB
        const attempt: TypingAttempt = {
          lessonId: lesson.id,
          timestamp: new Date().toISOString(),
          wpm,
          accuracy,
          textLength: target.length,
        };
        saveTypingAttempt(attempt).catch(console.error);

        saveProgress({
          lessonId: lesson.id,
          moduleId: 'typing',
          completedAt: new Date().toISOString(),
          score: accuracy,
          details: { wpm, accuracy, textLength: target.length },
        }).catch(console.error);
      }
    },
    [phase, startTime, target, lesson.id],
  );

  const handleRepeat = () => {
    setTyped('');
    setPhase('ready');
    setStartTime(null);
    setResult(null);
    setAiResponse(null);
  };

  const handleAskTutor = async () => {
    setAiLoading(true);
    setAiResponse(null);
    try {
      const context = isLesson
        ? `Typing lesson: "${lesson.titleFa}" (${lesson.title}). Practice text: "${lesson.text}". ${result ? `User got ${result.wpm} WPM and ${result.accuracy}% accuracy.` : ''}`
        : `Typing drill: "${lesson.titleFa}" (${lesson.title}). Practice text: "${lesson.text}". ${result ? `User got ${result.wpm} WPM and ${result.accuracy}% accuracy.` : ''}`;

      const res = await askTutor({
        module: 'typing',
        task: 'explain',
        context,
      });
      setAiResponse(res.explanation + (res.encouragement ? `\n\n${res.encouragement}` : ''));
    } catch {
      setAiResponse('متاسفانه خطایی رخ داد. لطفاً دوباره تلاش کنید.');
    } finally {
      setAiLoading(false);
    }
  };

  // Build the character-by-character display
  const renderTarget = () => {
    const chars: React.ReactNode[] = [];
    for (let i = 0; i < target.length; i++) {
      let className = '';
      if (i < typed.length) {
        className = typed[i] === target[i] ? 'correct' : 'incorrect';
      }
      const isCursor = i === typed.length && phase !== 'done';
      chars.push(
        <span key={i} className={`${className} ${isCursor ? 'cursor' : ''}`}>
          {target[i] === ' ' ? '\u00A0' : target[i]}
        </span>,
      );
    }
    return chars;
  };

  return (
    <div className="page">
      {/* Back button */}
      <button
        onClick={onBack}
        className="btn-secondary"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: 16,
          padding: '8px 16px',
          fontSize: '0.9rem',
        }}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
        بازگشت
      </button>

      {/* Title */}
      <h2 className="page-title">{lesson.titleFa}</h2>

      {/* Persian instruction (RTL) */}
      {isLesson && (
        <div
          className="card"
          style={{
            direction: 'rtl',
            textAlign: 'right',
            lineHeight: 2,
            whiteSpace: 'pre-line',
            fontSize: '0.95rem',
            color: 'var(--color-text-secondary)',
            marginBottom: 16,
          }}
        >
          {(lesson as TypingLesson).instruction}
        </div>
      )}

      {/* Target text display (LTR, monospace) */}
      <div className="typing-area">{renderTarget()}</div>

      {/* Text input (LTR) */}
      {phase !== 'done' && (
        <input
          ref={inputRef}
          type="text"
          dir="ltr"
          value={typed}
          onChange={handleChange}
          placeholder={phase === 'ready' ? 'Start typing here...' : ''}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          style={{
            marginTop: 16,
            fontFamily: "'Courier New', monospace",
            fontSize: '1.1rem',
            direction: 'ltr',
            textAlign: 'left',
          }}
        />
      )}

      {/* Timer / progress indicator */}
      {phase === 'typing' && (
        <div
          style={{
            marginTop: 12,
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '0.85rem',
            color: 'var(--color-text-secondary)',
            fontFamily: 'var(--font-en)',
            direction: 'ltr',
          }}
        >
          <span>
            {typed.length} / {target.length} characters
          </span>
          <span>
            {Math.round((typed.length / target.length) * 100)}% complete
          </span>
        </div>
      )}

      {/* Results */}
      {phase === 'done' && result && (
        <div style={{ marginTop: 20 }}>
          <div className="stat-grid">
            <div className="stat-card">
              <div className="stat-value" style={{ fontFamily: 'var(--font-en)' }}>
                {result.wpm}
              </div>
              <div className="stat-label">سرعت (WPM)</div>
            </div>
            <div className="stat-card">
              <div
                className="stat-value"
                style={{
                  fontFamily: 'var(--font-en)',
                  color:
                    result.accuracy >= 80
                      ? 'var(--color-success)'
                      : 'var(--color-error)',
                }}
              >
                {result.accuracy}%
              </div>
              <div className="stat-label">دقت</div>
            </div>
          </div>

          {/* Encouraging message for low accuracy */}
          {result.accuracy < 80 && (
            <div
              className="card"
              style={{
                textAlign: 'center',
                background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
                borderRight: '4px solid var(--color-warning)',
                fontSize: '1.05rem',
              }}
            >
              تمرین بیشتر کمکت می‌کنه! 💪
            </div>
          )}

          {/* Action buttons */}
          <div
            style={{
              display: 'flex',
              gap: 12,
              marginTop: 16,
              justifyContent: 'center',
            }}
          >
            <button className="btn-primary" onClick={handleRepeat}>
              تکرار
            </button>
            <button className="btn-secondary" onClick={onBack}>
              بازگشت
            </button>
          </div>

          {/* AI Tutor Button */}
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <button
              className="btn-secondary"
              onClick={handleAskTutor}
              disabled={aiLoading}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: 'linear-gradient(135deg, #e8f0fe 0%, #f0e6ff 100%)',
                border: '1px solid var(--color-primary-light)',
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              {aiLoading ? 'در حال فکر کردن...' : 'راهنمایی هوش مصنوعی'}
            </button>
          </div>

          {/* AI Loading indicator */}
          {aiLoading && (
            <div className="ai-loading">
              <span />
              <span />
              <span />
            </div>
          )}

          {/* AI Response */}
          {aiResponse && (
            <div className="ai-bubble" style={{ whiteSpace: 'pre-line' }}>
              {aiResponse}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
